import React, { useEffect, useState } from 'react';
import {
    Button,
    Card,
    Checkbox,
    Flex,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Select,
    Skeleton,
    Space,
    Table,
    Tooltip,
    Typography
} from 'antd';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import PropTypes from 'prop-types';

import { DeleteOutlined, EditOutlined, WarningOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from '@tt/dates/dayjs';

import { useAccountStore, useSettingStore, useUserState } from '../../store';
import axios from '../../utilities/axios';

import styles from './styles.module.scss';

const { Text, Title } = Typography;
const EMPTY_ARRAY = [];

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const COMMISSION_NUMBER_FIELDS = [
    'stock_base_commission',
    'stock_per_order_commission',
    'stock_per_quantity_commission',
    'stock_percentage_commission',
    'stock_base_fee',
    'stock_per_quantity_fee',
    'stock_percentage_fee',
    'stock_sec_fee',
    'options_base_commission',
    'options_per_order_commission',
    'options_per_quantity_commission',
    'options_percentage_commission',
    'options_base_fee',
    'options_per_quantity_fee',
    'options_percentage_fee'
];

const COMMISSION_FORM_SECTIONS = [
    {
        title: 'COMMISSIONS',
        className: '',
        fields: [
            { label: 'Stocks - Base Commission', name: 'stock_base_commission', prefix: '$' },
            {
                label: 'Stocks - Per Order Commission',
                name: 'stock_per_order_commission',
                prefix: '$'
            },
            {
                label: 'Stocks - Per Share Commission',
                name: 'stock_per_quantity_commission',
                prefix: '$'
            },
            { label: 'Stocks - % Commission', name: 'stock_percentage_commission', prefix: '%' },
            { label: 'Options - Base Commission', name: 'options_base_commission', prefix: '$' },
            {
                label: 'Options - Per Order Commission',
                name: 'options_per_order_commission',
                prefix: '$'
            },
            {
                label: 'Options - Per Share Commission',
                name: 'options_per_quantity_commission',
                prefix: '$'
            },
            { label: 'Options - % Commission', name: 'options_percentage_commission', prefix: '%' }
        ]
    },
    {
        title: 'FEES',
        className: styles.feesFields,
        fields: [
            { label: 'Stocks - Base Fee', name: 'stock_base_fee', prefix: '$' },
            { label: 'Stocks - Per Share Fee', name: 'stock_per_quantity_fee', prefix: '$' },
            { label: 'Stocks - % Fee', name: 'stock_percentage_fee', prefix: '%' },
            {
                label: 'Stocks - SEC Fee',
                name: 'stock_sec_fee',
                prefix: '%',
                extra: 'Applied only to SELL/SHORT stock executions. Enter as a percent, for example 0.00206 for $20.60 per $1,000,000.'
            },
            { label: 'Options - Base Fee', name: 'options_base_fee', prefix: '$' },
            { label: 'Options - Per Share Fee', name: 'options_per_quantity_fee', prefix: '$' },
            { label: 'Options - % Fee', name: 'options_percentage_fee', prefix: '%' }
        ]
    }
];

const fetchCommissionProfiles = async (scope = 'mine') => {
    try {
        const { data } = await axios.get('crud/commission', { params: { scope } });
        return data;
    } catch (error) {
        if (error.response?.status === 404) {
            return [];
        }

        throw error;
    }
};

const buildCommissionFormValues = (profile = null, isAdmin = false) => {
    const baseValues = {
        name: profile?.name || '',
        adminSharedProfile: profile?.adminSharedProfile ?? Boolean(isAdmin)
    };

    COMMISSION_NUMBER_FIELDS.forEach((field) => {
        if (profile && typeof profile[field] !== 'undefined' && profile[field] !== null) {
            baseValues[field] = profile[field];
        } else {
            baseValues[field] = undefined;
        }
    });

    return baseValues;
};

const normalizeCommissionValues = (values) => {
    const normalizedValues = { ...values };

    normalizedValues.name = normalizedValues.name?.trim?.() || '';
    normalizedValues.adminSharedProfile = Boolean(normalizedValues.adminSharedProfile);

    COMMISSION_NUMBER_FIELDS.forEach((field) => {
        if (
            normalizedValues[field] === '' ||
            normalizedValues[field] === null ||
            typeof normalizedValues[field] === 'undefined'
        ) {
            normalizedValues[field] = 0;
        }
    });

    return normalizedValues;
};

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const formatPercentage = (value) => {
    const numericValue = Number(value || 0);

    if (Number.isNaN(numericValue)) return '0%';

    return `${numericValue.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6
    })}%`;
};

const getCreatorName = (profile) => {
    const firstName = profile?.creator?.firstName?.trim?.() || '';
    const lastName = profile?.creator?.lastName?.trim?.() || '';
    const fullName = `${firstName} ${lastName}`.trim();

    if (fullName.length > 0) {
        return fullName;
    }

    return profile?.creator?.email || 'Unknown';
};

const isAdminDefaultProfile = (profileId, adminSettings) => {
    const pctDefault = adminSettings?.liveUploadDefaults?.pct?.commissionProfileId;
    const pttDefault = adminSettings?.liveUploadDefaults?.ptt?.commissionProfileId;

    return [pctDefault, pttDefault].filter(Boolean).includes(profileId);
};

const getProfileScopeLabel = (profile, adminSettings) => {
    if (profile?.adminSharedProfile) return 'Admin Shared';
    if (isAdminDefaultProfile(profile?._id, adminSettings)) return 'Current Admin Default';
    return 'Personal';
};

const formatCommissionOptionLabel = (profile) => {
    const creatorName = getCreatorName(profile);

    if (creatorName === 'Unknown') {
        return profile.name;
    }

    return `${profile.name} - ${creatorName}`;
};

function CommissionProfileFields({ isAdmin }) {
    return (
        <>
            <div className={styles.mainFields}>
                <Form.Item
                    className={styles.primaryField}
                    label="Profile Name"
                    name="name"
                    rules={[
                        {
                            required: true,
                            message: 'Add a profile name to identify this commission rule.'
                        }
                    ]}
                >
                    <Input />
                </Form.Item>
                {isAdmin && (
                    <Form.Item
                        className={styles.primaryField}
                        name="adminSharedProfile"
                        valuePropName="checked"
                        label="Admin Upload Profile"
                    >
                        <Checkbox>
                            Share with all admins and allow use for PCT/BBT upload defaults.
                        </Checkbox>
                    </Form.Item>
                )}
                <Text type="secondary" className={styles.formNote}>
                    Per order commission applies once per uploaded execution order. System-generated
                    adjustments are excluded.
                </Text>
            </div>

            <div className={styles.commissionFields}>
                {COMMISSION_FORM_SECTIONS.map((section) => (
                    <div
                        key={section.title}
                        className={`${styles.commissionSection} ${section.className}`}
                    >
                        <Title level={4} className={styles.sectionTitle}>
                            {section.title}
                        </Title>
                        {section.fields.map((field) => (
                            <Form.Item
                                className={styles.commissionField}
                                key={field.name}
                                label={field.label}
                                name={field.name}
                                extra={
                                    field.extra ? (
                                        <span className={styles.fieldExtra}>{field.extra}</span>
                                    ) : undefined
                                }
                                rules={[
                                    {
                                        required: false
                                    }
                                ]}
                            >
                                <InputNumber
                                    prefix={field.prefix}
                                    formatter={
                                        field.prefix === '%' ? (value) => `${value}` : undefined
                                    }
                                    min={0}
                                    step={field.prefix === '%' ? 0.000001 : 0.01}
                                    style={{ width: '100%' }}
                                    placeholder="0"
                                />
                            </Form.Item>
                        ))}
                    </div>
                ))}
            </div>
        </>
    );
}

CommissionProfileFields.propTypes = {
    isAdmin: PropTypes.bool.isRequired
};

export default function CommissionsPage() {
    const { allCommissions, setAllCommissions } = useAccountStore((state) => state);
    const { advancedSettings, setAdvancedSettings } = useSettingStore((state) => state);
    const { getUser } = useUserState();
    const user = getUser();
    const isAdmin = user?.role === 'admin';

    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    const [editingProfile, setEditingProfile] = useState(null);
    const [removingCommission, setRemovingCommission] = useState('');
    const [savingLiveUploadDefault, setSavingLiveUploadDefault] = useState('');

    const refreshCommissionQueries = async (
        refetchCommissionProfiles,
        refetchSharedAdminProfiles,
        refetchAdminSettings
    ) => {
        await refetchCommissionProfiles();

        if (isAdmin) {
            await refetchSharedAdminProfiles();
            await refetchAdminSettings();
        }
    };

    const {
        data: commissionProfilesData,
        refetch: refetchCommissionProfiles,
        isLoading
    } = useQuery(
        ['commission-profiles', isAdmin ? 'accessible' : 'mine'],
        () => fetchCommissionProfiles(isAdmin ? 'accessible' : 'mine'),
        { enabled: Boolean(user?.token) }
    );

    const { data: sharedAdminProfilesData, refetch: refetchSharedAdminProfiles } = useQuery(
        ['commission-profiles', 'shared-admin'],
        () => fetchCommissionProfiles('shared-admin'),
        {
            enabled: Boolean(user?.token) && isAdmin
        }
    );

    const { data: adminSettings, refetch: refetchAdminSettings } = useQuery(
        ['journal-admin-settings'],
        () => axios.get('crud/journal-admin-settings').then(({ data }) => data),
        { enabled: Boolean(user?.token) && isAdmin }
    );

    const createCommission = (values) =>
        axios.post('crud/commission', values).then(({ data }) => data);
    const updateCommission = ({ id, values }) =>
        axios.put(`crud/commission/${id}`, values).then(({ data }) => data);

    const createCommissionMutation = useMutation(createCommission);
    const updateCommissionMutation = useMutation(updateCommission);
    const commissionProfiles = commissionProfilesData || EMPTY_ARRAY;
    const sharedAdminProfiles = sharedAdminProfilesData || EMPTY_ARRAY;

    useEffect(() => {
        const ownedProfiles = commissionProfiles.filter((profile) => profile.userRef === user?.id);
        const previousIds = (allCommissions || []).map((profile) => profile?._id).join(',');
        const nextIds = ownedProfiles.map((profile) => profile?._id).join(',');

        if (previousIds === nextIds) return;

        setAllCommissions(ownedProfiles);
    }, [allCommissions, commissionProfiles, setAllCommissions, user?.id]);

    const clearDeletedCommissionSelections = (commissionId) => {
        const nextCommissions = { ...advancedSettings.commissions };
        const nextOptions = { ...advancedSettings.options };
        let didChange = false;

        Object.keys(nextCommissions).forEach((settingKey) => {
            if (nextCommissions[settingKey] !== commissionId) return;

            const filteredOptions = (nextOptions[settingKey] || []).filter(
                (item) => !['forceOverwriteCommission', 'applyFileCommissions'].includes(item)
            );

            nextCommissions[settingKey] = null;
            nextOptions[settingKey] = filteredOptions.includes('noCommission')
                ? filteredOptions
                : [...filteredOptions, 'noCommission'];
            didChange = true;
        });

        if (!didChange) return;

        setAdvancedSettings('options', nextOptions);
        setAdvancedSettings('commissions', nextCommissions);
    };

    const onFinish = async (values) => {
        const payload = normalizeCommissionValues(values);

        try {
            await createCommissionMutation.mutateAsync(payload);
            form.resetFields();
            form.setFieldsValue(buildCommissionFormValues(null, isAdmin));
            await refreshCommissionQueries(
                refetchCommissionProfiles,
                refetchSharedAdminProfiles,
                refetchAdminSettings
            );
            message.success('Commission profile created.');
        } catch {
            message.error('Something went wrong, please try again.');
        }
    };

    const onEditFinish = async (values) => {
        if (!editingProfile?._id) return;

        const payload = normalizeCommissionValues(values);

        try {
            await updateCommissionMutation.mutateAsync({
                id: editingProfile._id,
                values: payload
            });

            await refreshCommissionQueries(
                refetchCommissionProfiles,
                refetchSharedAdminProfiles,
                refetchAdminSettings
            );
            setEditingProfile(null);
            editForm.resetFields();
            message.success('Commission profile updated.');
        } catch {
            message.error('Something went wrong, please try again.');
        }
    };

    const deleteCommissionByID = async (id) => {
        setRemovingCommission(id);

        try {
            await axios.delete(`crud/commission/${id}`);
            clearDeletedCommissionSelections(id);
            await refreshCommissionQueries(
                refetchCommissionProfiles,
                refetchSharedAdminProfiles,
                refetchAdminSettings
            );
            message.success('Commission deleted.');
        } catch {
            message.error('Something went wrong, please try again.');
        } finally {
            setRemovingCommission('');
        }
    };

    const updateLiveUploadDefault = async (companyKey, commissionId) => {
        const selectedProfile = sharedAdminProfiles.find((profile) => profile._id === commissionId);

        setSavingLiveUploadDefault(companyKey);

        try {
            await axios.put('crud/journal-admin-settings', {
                liveUploadDefaults: {
                    [companyKey]: {
                        commissionProfileId: commissionId || null,
                        commissionProfileName: selectedProfile?.name || null
                    }
                }
            });

            await refreshCommissionQueries(
                refetchCommissionProfiles,
                refetchSharedAdminProfiles,
                refetchAdminSettings
            );
            message.success(
                commissionId ? 'Live upload default updated.' : 'Live upload default cleared.'
            );
        } catch {
            message.error('Something went wrong, please try again.');
        } finally {
            setSavingLiveUploadDefault('');
        }
    };

    const openEditModal = (profile) => {
        setEditingProfile(profile);
        editForm.setFieldsValue(buildCommissionFormValues(profile, isAdmin));
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 220
        },
        isAdmin && {
            title: 'Scope',
            key: 'scope',
            width: 170,
            render: (_, profile) => getProfileScopeLabel(profile, adminSettings)
        },
        isAdmin && {
            title: 'Created By',
            key: 'creator',
            width: 220,
            render: (_, profile) => getCreatorName(profile)
        },
        {
            title: 'Stocks Per Order Commission',
            dataIndex: 'stock_per_order_commission',
            key: 'stock_per_order_commission',
            render: formatCurrency
        },
        {
            title: 'Stocks Base Commission',
            dataIndex: 'stock_base_commission',
            key: 'stock_base_commission',
            render: formatCurrency
        },
        {
            title: 'Stocks Per Share Commission',
            dataIndex: 'stock_per_quantity_commission',
            key: 'stock_per_quantity_commission',
            render: formatCurrency
        },
        {
            title: 'Stocks Commission',
            dataIndex: 'stock_percentage_commission',
            key: 'stock_percentage_commission',
            render: formatPercentage
        },
        {
            title: 'Stocks Base Fee',
            dataIndex: 'stock_base_fee',
            key: 'stock_base_fee',
            render: formatCurrency
        },
        {
            title: 'Stocks Per Share Fee',
            dataIndex: 'stock_per_quantity_fee',
            key: 'stock_per_quantity_fee',
            render: formatCurrency
        },
        {
            title: 'Stocks Fee',
            dataIndex: 'stock_percentage_fee',
            key: 'stock_percentage_fee',
            render: formatPercentage
        },
        {
            title: 'Stocks SEC Fee',
            dataIndex: 'stock_sec_fee',
            key: 'stock_sec_fee',
            render: formatPercentage
        },
        {
            title: 'Options Per Order Commission',
            dataIndex: 'options_per_order_commission',
            key: 'options_per_order_commission',
            render: formatCurrency
        },
        {
            title: 'Options Base Commission',
            dataIndex: 'options_base_commission',
            key: 'options_base_commission',
            render: formatCurrency
        },
        {
            title: 'Options Per Share Commission',
            dataIndex: 'options_per_quantity_commission',
            key: 'options_per_quantity_commission',
            render: formatCurrency
        },
        {
            title: 'Options Commission',
            dataIndex: 'options_percentage_commission',
            key: 'options_percentage_commission',
            render: formatPercentage
        },
        {
            title: 'Options Base Fee',
            dataIndex: 'options_base_fee',
            key: 'options_base_fee',
            render: formatCurrency
        },
        {
            title: 'Options Per Share Fee',
            dataIndex: 'options_per_quantity_fee',
            key: 'options_per_quantity_fee',
            render: formatCurrency
        },
        {
            title: 'Options Fee',
            dataIndex: 'options_percentage_fee',
            key: 'options_percentage_fee',
            render: formatPercentage
        },
        {
            title: '',
            dataIndex: '',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, profile) => (
                <Space>
                    <Tooltip title="Edit commission profile">
                        <Button
                            size="small"
                            type="outlined"
                            shape="circle"
                            icon={<EditOutlined />}
                            onClick={() => openEditModal(profile)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete Commission Profile?"
                        description="Deleting this commission profile will remove it permanently. Are you sure?"
                        onConfirm={() => deleteCommissionByID(profile._id)}
                        okText="Yes"
                        cancelText="No"
                        placement="left"
                    >
                        <Tooltip
                            title={
                                <>
                                    <WarningOutlined /> Remove Single Commission Profile
                                </>
                            }
                            color="#E57373"
                        >
                            <Button
                                size="small"
                                loading={removingCommission === profile._id}
                                type="outlined"
                                shape="circle"
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ].filter(Boolean);

    return (
        <div className={styles.page}>
            {isAdmin && (
                <Card
                    className={styles.adminDefaultsCard}
                    title="Admin Upload Default Commission Profiles"
                    size="small"
                    bodyStyle={{ padding: 16 }}
                >
                    <Flex vertical gap={10} className={styles.liveUploadDefaults}>
                        <div className={styles.liveUploadRow}>
                            <Text className={styles.liveUploadLabel}>Peak Capital Trading</Text>
                            <Select
                                allowClear
                                showSearch
                                size="small"
                                className={styles.liveUploadSelect}
                                placeholder="Use file commissions"
                                value={
                                    adminSettings?.liveUploadDefaults?.pct?.commissionProfileId ||
                                    undefined
                                }
                                loading={savingLiveUploadDefault === 'pct'}
                                options={sharedAdminProfiles.map((profile) => ({
                                    label: formatCommissionOptionLabel(profile),
                                    value: profile._id
                                }))}
                                onChange={(value) => updateLiveUploadDefault('pct', value)}
                                filterOption={(input, option) =>
                                    (option?.label ?? '')
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                            />
                        </div>
                        <div className={styles.liveUploadRow}>
                            <Text className={styles.liveUploadLabel}>Bear Bull Traders</Text>
                            <Select
                                allowClear
                                showSearch
                                size="small"
                                className={styles.liveUploadSelect}
                                placeholder="Use file commissions"
                                value={
                                    adminSettings?.liveUploadDefaults?.ptt?.commissionProfileId ||
                                    undefined
                                }
                                loading={savingLiveUploadDefault === 'ptt'}
                                options={sharedAdminProfiles.map((profile) => ({
                                    label: formatCommissionOptionLabel(profile),
                                    value: profile._id
                                }))}
                                onChange={(value) => updateLiveUploadDefault('ptt', value)}
                                filterOption={(input, option) =>
                                    (option?.label ?? '')
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                            />
                        </div>
                        <Text type="secondary" className={styles.liveUploadNote}>
                            Only admin-shared profiles appear here. The creator is shown so other
                            admins can verify and edit shared defaults.
                        </Text>
                    </Flex>
                </Card>
            )}

            <Form
                className={styles.commissionForm}
                name="commission-form"
                form={form}
                layout="horizontal"
                initialValues={buildCommissionFormValues(null, isAdmin)}
                onFinish={onFinish}
                autoComplete="off"
            >
                <CommissionProfileFields isAdmin={isAdmin} />

                <Form.Item
                    className={styles.submitRow}
                    wrapperCol={{
                        span: 24
                    }}
                >
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={createCommissionMutation.isLoading}
                    >
                        Add New Rule
                    </Button>
                </Form.Item>
            </Form>

            <Title level={5}>COMMISSIONS & FEES PROFILES</Title>
            <Card className={styles.profilesCard}>
                {isLoading ? (
                    <Skeleton active paragraph={{ rows: 12 }} />
                ) : (
                    <Table
                        columns={columns}
                        dataSource={commissionProfiles}
                        scroll={{ x: 2200, y: '20rem' }}
                        rowKey="_id"
                        pagination={false}
                    />
                )}
            </Card>

            <Modal
                title={editingProfile ? `Edit ${editingProfile.name}` : 'Edit Commission Profile'}
                open={Boolean(editingProfile)}
                onCancel={() => {
                    setEditingProfile(null);
                    editForm.resetFields();
                }}
                footer={null}
                destroyOnClose
                width={900}
            >
                <Form
                    className={styles.commissionForm}
                    form={editForm}
                    layout="horizontal"
                    onFinish={onEditFinish}
                    autoComplete="off"
                >
                    <CommissionProfileFields isAdmin={isAdmin} />

                    <Form.Item
                        className={styles.submitRow}
                        wrapperCol={{
                            span: 24
                        }}
                    >
                        <Space>
                            <Button
                                onClick={() => {
                                    setEditingProfile(null);
                                    editForm.resetFields();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={updateCommissionMutation.isLoading}
                            >
                                Save Changes
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
